import { api, type Query } from '@/lib/api';
import { qk } from '@/lib/queryClient';
import type { NotificationItem } from '@/lib/types';

/** Server-backed inbox + preferences. The database is the only source of truth (no local storage). */

export type NotificationCategory = 'people' | 'engagements' | 'reviews' | 'approvals' | 'assets' | 'access' | 'reminders' | 'billing' | 'announcements' | 'tasks';

export interface InboxItem extends NotificationItem {
  type?: string;
  category?: NotificationCategory;
  typeLabel?: string;
  tenantName?: string;
  recipientName?: string;
}

export interface InboxPage {
  items: InboxItem[];
  unread: number;
  /** Absent on the legacy response shape. */
  page?: { nextCursor: string | null; limit: number };
}

export interface InboxFilters {
  unread?: boolean;
  type?: string;
  category?: NotificationCategory | '';
  engagementId?: string;
  from?: string;
  to?: string;
}

export const PAGE_SIZES = [5, 10, 20, 50, 100] as const;
export type PageSize = (typeof PAGE_SIZES)[number];

export const notificationKeys = {
  all: qk.notifications,
  bell: [...qk.notifications, 'bell'] as const,
  inbox: (filters: InboxFilters, limit: number) => [...qk.notifications, 'inbox', filters, limit] as const,
  preferences: [...qk.notifications, 'preferences'] as const,
};

export function fetchInbox(opts: { limit: number; cursor?: string | null; filters?: InboxFilters; signal?: AbortSignal; platform?: boolean }): Promise<InboxPage> {
  const f = opts.filters ?? {};
  const query: Query = {
    limit: opts.limit,
    cursor: opts.cursor ?? undefined,
    unread: f.unread ? 'true' : undefined,
    type: f.type || undefined,
    category: f.category || undefined,
    engagementId: f.engagementId || undefined,
    from: f.from || undefined,
    to: f.to || undefined,
  };
  return api.get<InboxPage>(opts.platform ? '/platform/notifications' : '/me/notifications', query, { signal: opts.signal });
}

export const markRead = (body: { ids?: string[]; all?: boolean }, platform = false) => api.post<{ updated: number; unread?: number }>(`${platform ? '/platform' : '/me'}/notifications/read`, body);
export const markOne = (id: string, read: boolean, platform = false) => api.post<{ id: string; readAt: string | null; unread: number }>(`${platform ? '/platform' : '/me'}/notifications/${id}/${read ? 'read' : 'unread'}`);
export const dismiss = (id: string, platform = false) => api.raw<{ data: { id: string; dismissedAt: string; unread: number } }>(`${platform ? '/platform' : '/me'}/notifications/${id}`, { method: 'DELETE' }).then((r) => r.data);

/** Only same-origin app paths are followed; otherwise fall back to the engagement for this context. */
export function notificationHref(n: Pick<NotificationItem, 'link' | 'engagementId'>, kind: 'staff' | 'client' | undefined): string | null {
  if (n.link && n.link.startsWith('/') && !n.link.startsWith('//')) return n.link;
  if (n.engagementId) return kind === 'client' ? `/portal?engagement=${n.engagementId}` : `/engagements/${n.engagementId}`;
  return null;
}

/* ---------------------------------------------------------------- preferences */

export interface TypePreference {
  type: string;
  label: string;
  description: string;
  category: NotificationCategory;
  overridable: boolean;
  emailAvailable: boolean;
  inApp: boolean;
  email: boolean;
  overrides: { inApp: boolean | null; email: boolean | null };
}

export interface NotificationPreferencesPayload {
  channels: { inApp: boolean; email: boolean };
  digest: boolean;
  types: TypePreference[];
}

export interface PreferencesUpdate {
  channels?: { inApp?: boolean; email?: boolean };
  digest?: boolean;
  types?: Record<string, { inApp?: boolean | null; email?: boolean | null }>;
}

export const fetchPreferences = (signal?: AbortSignal) => api.get<NotificationPreferencesPayload>('/me/notification-preferences', undefined, { signal });
export const savePreferences = (body: PreferencesUpdate) => api.put<NotificationPreferencesPayload>('/me/notification-preferences', body);

export const CATEGORY_LABELS: Record<NotificationCategory, string> = {
  announcements: 'Announcements',
  tasks: 'Tasks',
  people: 'People',
  engagements: 'Engagements',
  reviews: 'Reviews',
  approvals: 'Approvals',
  assets: 'Files',
  access: 'Access',
  reminders: 'Reminders',
  billing: 'Billing',
};
